/**
 * Das Störungsblatt.
 *
 * Eine gemeldete Störung von allen Seiten: was gemeldet wurde, welche Einheit
 * betroffen ist (Waschschleuderautomat oder Taktwaschanlage) und was die
 * Technik daraus gemacht hat. Reines Ablesen — erfasst und beendet wird in
 * Betrieb → Störungen.
 *
 * Dieselbe Seite hängt an zwei Wegen: `/admin/disruptions/:id` (Büro) und
 * `/tech/disruptions/:id` (Haustechniker). Der Zustand wird über die
 * Zustands-Token gezeigt — offen = stoer, in Arbeit und Warten auf Teile =
 * stand, bestätigt = lauf.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { LeitstandRahmen } from '../components/leitstand/LeitstandRahmen';
import { Karte, Marke, Notiz, Zustand } from '../components/leitstand';
import type { ZustandsArt } from '../components/leitstand';
import { ModuleBadge } from '../components/ModuleBadge';
import { formatBerlinDateTimeDE, formatBerlinDateLongDE } from '../utils/berlinDate';

interface Disruption {
  id: string;
  shiftDate: string;
  shiftType: string;
  startedAt: string;
  endedAt: string | null;
  categoryId: string | null;
  categoryCodeSnapshot: string | null;
  categoryNameSnapshot: string | null;
  category: { id: string; code: string; name: string; color: string | null } | null;
  description: string | null;
  techStatus: string;
  rootCause: string | null;
  repairAction: string | null;
  partsUsed: string | null;
  workMinutes: number | null;
  techNotes: string | null;
  machine: { id: string; name: string } | null;
  station: { id: string; name: string } | null;
  recordedBy: { displayName: string };
  technician: { displayName: string } | null;
}

/** Der Name aus dem Schnappschuss geht vor: Er zeigt, wie die Kategorie zur Zeit der Meldung hieß. */
const kategorieName = (d: Disruption): string => d.categoryNameSnapshot ?? d.category?.name ?? '—';

const STATUS: Record<string, { text: string; art: ZustandsArt }> = {
  OPEN: { text: 'Offen', art: 'stoerung' },
  IN_PROGRESS: { text: 'In Bearbeitung', art: 'warnung' },
  WAITING_PARTS: { text: 'Warte auf Teile', art: 'warnung' },
  RESOLVED_CONFIRMED: { text: 'Bestätigt', art: 'gut' },
  REOPENED: { text: 'Wieder geöffnet', art: 'stoerung' },
};

const URSACHEN: Record<string, string> = {
  WEAR: 'Verschleiß',
  DEFECT: 'Defekt',
  CONTAMINATION: 'Verschmutzung',
  OPERATOR_ERROR: 'Bedienfehler',
  POWER_ISSUE: 'Strom/Elektrik',
  SOFTWARE: 'Software',
  UNKNOWN: 'Unbekannt',
  OTHER: 'Sonstiges',
};

const SCHICHTEN: Record<string, string> = {
  EARLY: 'Frühschicht',
  LATE: 'Spätschicht',
  SATURDAY: 'Samstagsschicht',
};

const KNOPF_LEISE =
  'rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 font-zahl text-xs text-tinte-2';

/** Eine Zeile „Beschriftung — Wert" innerhalb einer Karte. */
function Feld({ marke, wert, zahl = false }: { marke: string; wert: string; zahl?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <Marke>{marke}</Marke>
      <span className={`text-right text-sm ${zahl ? 'font-zahl tabular-nums' : ''}`}>{wert}</span>
    </div>
  );
}

/** Fließtext mit Beschriftung darüber — für alles, was länger als eine Zeile wird. */
function Text({ marke, wert }: { marke: string; wert: string }) {
  return (
    <div className="border-t border-linie pt-3">
      <Marke>{marke}</Marke>
      <p className="mt-1 text-sm text-tinte-2">{wert}</p>
    </div>
  );
}

export default function DisruptionDetailPage() {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [disruption, setDisruption] = useState<Disruption | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');

  const lade = useCallback(async () => {
    if (!id) return;
    setLaden(true);
    setFehler('');
    try {
      const res = await fetch(`/api/disruptions/${id}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Störung nicht gefunden');
      setDisruption(await res.json());
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Störung nicht gefunden');
    } finally {
      setLaden(false);
    }
  }, [id]);

  useEffect(() => {
    void lade();
  }, [lade]);

  const stand = disruption ? STATUS[disruption.techStatus] : undefined;

  return (
    <LeitstandRahmen
      titel="Störung"
      benutzer={user?.displayName}
      werkzeuge={
        <span className="flex items-center gap-3">
          <ModuleBadge module="disruptions" />
          {stand && <Zustand art={stand.art}>{stand.text}</Zustand>}
          <button
            onClick={() => void lade()}
            disabled={laden}
            className={`${KNOPF_LEISE} disabled:opacity-50`}
          >
            Aktualisieren
          </button>
        </span>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <Link to="/admin/disruptions" className="text-[13px] text-tinte-3 hover:text-tinte-2">
            ← Zurück zur Übersicht
          </Link>
          {disruption && (
            <p className="text-sm text-tinte-2">
              {formatBerlinDateLongDE(disruption.shiftDate)} —{' '}
              {SCHICHTEN[disruption.shiftType] || disruption.shiftType}
            </p>
          )}
        </div>

        {fehler && (
          <Notiz titel="Konnte nicht laden">
            {fehler}{' '}
            <button onClick={() => void lade()} className="underline">
              Erneut versuchen
            </button>
          </Notiz>
        )}

        {laden && !fehler && (
          <p className="py-12 text-center text-sm text-tinte-3">Lade Störung …</p>
        )}

        {!laden && !fehler && disruption && (
          <div className="grid gap-5 md:grid-cols-2">
            <Karte titel="Störungsdaten" marke="wie gemeldet">
              <div className="flex flex-col gap-3">
                <div className="flex items-baseline justify-between gap-4">
                  <Marke>Zustand</Marke>
                  {stand ? (
                    <Zustand art={stand.art}>{stand.text}</Zustand>
                  ) : (
                    <span className="font-zahl text-[11px] text-tinte-3">
                      {disruption.techStatus}
                    </span>
                  )}
                </div>
                <Feld marke="Kategorie" wert={kategorieName(disruption)} />
                <Feld marke="Beginn" wert={formatBerlinDateTimeDE(disruption.startedAt)} zahl />
                {disruption.endedAt && (
                  <Feld marke="Ende" wert={formatBerlinDateTimeDE(disruption.endedAt)} zahl />
                )}
                <Text marke="Beschreibung" wert={disruption.description || '—'} />
              </div>
            </Karte>

            <Karte titel="Betroffene Einheit" marke="Waschschleuderautomat oder Taktwaschanlage">
              <div className="flex flex-col gap-3">
                {disruption.machine && <Feld marke="Maschine" wert={disruption.machine.name} />}
                {disruption.station && <Feld marke="Station" wert={disruption.station.name} />}
                <Feld marke="Gemeldet von" wert={disruption.recordedBy.displayName} />
              </div>
            </Karte>

            <Karte titel="Bearbeitung durch die Technik" marke="in wash-ops nachgetragen">
              <div className="flex flex-col gap-3">
                <Feld marke="Techniker" wert={disruption.technician?.displayName || '—'} />
                <Feld
                  marke="Ursache"
                  wert={URSACHEN[disruption.rootCause || ''] || disruption.rootCause || '—'}
                />
                {disruption.workMinutes != null && (
                  <Feld marke="Arbeitszeit" wert={`${disruption.workMinutes} min`} zahl />
                )}
                {disruption.repairAction && (
                  <Text marke="Maßnahme" wert={disruption.repairAction} />
                )}
                {disruption.partsUsed && (
                  <Text marke="Verwendete Teile" wert={disruption.partsUsed} />
                )}
                {disruption.techNotes && <Text marke="Notizen" wert={disruption.techNotes} />}
              </div>
            </Karte>

            {disruption.techStatus === 'RESOLVED_CONFIRMED' && (
              <Notiz titel="Störung behoben">
                <div className="flex flex-col gap-1.5">
                  <Zustand art="gut">bestätigt</Zustand>
                  {disruption.endedAt && (
                    <span className="font-zahl text-[13px] text-tinte-2">
                      Behoben am {formatBerlinDateTimeDE(disruption.endedAt)}
                    </span>
                  )}
                </div>
              </Notiz>
            )}
          </div>
        )}
      </div>
    </LeitstandRahmen>
  );
}
